/**
 * NOLAS 2026, (c) Mikaël FOURNY.
 * src/i18n/fr.presentation.ts
 *
 * Messages de la PRÉSENTATION QUI DÉFILE (/admin/presentation, écran 16:9
 * de diffusion, non tactile) et des ajouts du MUR DE PILOTAGE
 * (/admin/dashboard/wall) du 2026-10-04.
 *
 * Charte (partie 4, « Écran d'affichage 1920 x 1080 ») : vingt mots au plus
 * par diapositive hors date et lieu ; le reste vient des données.
 * Vocabulaire piloté par l'événement : jamais « salon », « atelier »,
 * « exposant » ni nom d'événement en dur, jetons {eventXxx} et {lexXxx}.
 * Apostrophe typographique (’) partout ; aucun tiret cadratin.
 * Fusionné dans fr.ts.
 *
 * Mikael - chef de projet - 2026-10-04
 */
export const messagesPresentationFr: Record<string, string> = {
  // ── Entrée du back-office et titre de l'écran ──────────────────────────
  'adminNav.presentation': 'Présentation qui défile',
  'acces.titre.adminPresentation': 'Présentation qui défile (écran 16:9)',
  'presentation.titre': 'Présentation {eventTypeOf}',

  // ── Commandes (apparaissent au mouvement de la souris) ─────────────────
  'presentation.controles.label': 'Commandes de la présentation',
  'presentation.controles.precedente': 'Diapositive précédente',
  'presentation.controles.suivante': 'Diapositive suivante',
  'presentation.controles.pause': 'Mettre en pause',
  'presentation.controles.lecture': 'Reprendre le défilement',
  'presentation.controles.pleinEcran': 'Plein écran',
  'presentation.controles.quitterPleinEcran': 'Quitter le plein écran',
  'presentation.controles.fenetre': 'Ouvrir dans une fenêtre dédiée',
  'presentation.controles.quitter': 'Quitter',
  'presentation.controles.position': '{n} / {total}',
  'presentation.controles.aide': 'Flèches : changer · Espace : pause · F : plein écran',
  'presentation.horsLigne': 'Hors ligne : données de {heure}',
  'presentation.bientot': 'Les listes ouvrent très bientôt au public.',

  // ── Diapositives ───────────────────────────────────────────────────────
  'presentation.organisateurs.titre': 'Organisé par',
  'presentation.partenaires.titre': 'Avec le soutien de',

  'presentation.chiffres.titre': 'En',
  'presentation.chiffres.accent': 'chiffres',
  'presentation.chiffres.exposants':
    '{n, plural, one {{lexExhibitorBare}} other {{lexExhibitorPlural}}}',
  'presentation.chiffres.offres': '{n, plural, one {offre} other {offres}}',
  'presentation.chiffres.postes': '{n, plural, one {poste à pourvoir} other {postes à pourvoir}}',
  'presentation.chiffres.alternance':
    '{n, plural, one {poste en alternance} other {postes en alternance}}',
  'presentation.chiffres.mention': 'Au {date}, susceptibles d’évoluer',

  'presentation.exposants.titreAvant': 'Ils seront',
  'presentation.exposants.titrePendant': 'Ils sont',
  // Après la fin de l'événement. Mikael - chef de projet - 2026-10-05
  'presentation.exposants.titreApres': 'Ils étaient',
  'presentation.exposants.accent': 'là',

  'presentation.offres.titre': 'Les offres',
  'presentation.offres.accent': 'qui recrutent le plus',
  'presentation.offres.postes': '{n, plural, one {# poste} other {# postes}}',

  'presentation.secteurs.titre': 'Les métiers',
  'presentation.secteurs.accent': 'qui recrutent',
  'presentation.secteurs.postes': '{n, plural, one {# poste} other {# postes}}',

  'presentation.ateliers.titre': '{lexWorkshopPluralCap}',
  'presentation.ateliers.accentAvant': 'au programme',
  'presentation.ateliers.accentPendant': 'en ce moment',
  'presentation.ateliers.enCours': 'En cours',
  'presentation.ateliers.aVenir': 'À venir',

  'presentation.plan.titre': 'Le plan',
  'presentation.plan.accent': 'pour vous repérer',
  'presentation.plan.alt': 'Plan {eventTypeOf}',

  'presentation.inscription.titre': 'Inscrivez-vous,',
  'presentation.inscription.accent': 'déposez votre CV',
  'presentation.inscription.texte': 'Scannez avec votre téléphone',

  'presentation.application.titre': 'L’application',
  'presentation.application.accent': 'dans votre poche',
  'presentation.application.point1': 'Toutes les offres, par métier',
  'presentation.application.point2': '{lexExhibitorPluralCap} et leurs postes',
  'presentation.application.point3': 'Programme et plan',
  'presentation.application.point4': 'Votre badge, votre CV',

  'presentation.qr.alt': 'QR code vers {adresse}',
  'presentation.page': '{page} / {pages}',

  // ── Mur de pilotage, ajouts du 2026-10-04 ──────────────────────────────
  'dashboardWall.kpi.label': 'Indicateurs clés',
  // 2026-10-05 : présents d'après l'entrée et la sortie (équipe de scan), et
  // personnes vues récemment sur un stand. Mikael - chef de projet - 2026-10-05
  // 2026-10-05 : rappel du sondage au scan de sortie (écran de contrôle et
  // réglage dans Types de contrôle). Mikael - chef de projet - 2026-10-05
  'controle.rappelSondage.titre': 'Sondage non rempli',
  'controle.rappelSondage.texte':
    'Invitez la personne à répondre à « {titre} » avant de partir : dans l’application, Plus, puis « Donner mon avis ». Deux minutes suffisent.',
  'controlTypes.rappelSondage.titre': 'Rappel du sondage à la sortie',
  'controlTypes.rappelSondage.aide':
    'Activé : quand l’équipe scanne une personne au point « Sortie », l’écran signale qu’elle n’a pas répondu au sondage qui lui est destiné, pour l’inviter à le remplir. Le passage est enregistré dans tous les cas.',
  'controlTypes.rappelSondage.actif': 'Activé',
  'controlTypes.rappelSondage.inactif': 'Désactivé',
  'controlTypes.rappelSondage.erreur': 'Le réglage n’a pas pu être enregistré. Réessayez.',
  'dashboardWall.kpi.presentsPortes': 'Présents (entrées − sorties)',
  'dashboardWall.kpi.actifsStands':
    'Actifs sur les stands ({minutes} min) : {n}',
  'dashboardWall.countdown.unknown': 'En direct',
  'dashboardWall.top.title': 'Classement en direct',
  'dashboardWall.top.empty': 'Aucun passage pour le moment',
  'dashboardWall.top.count': '{n, plural, one {# personne} other {# personnes}}',
};
